import { AlertCircle, Plus, RefreshCw } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaPageHeader } from "@/shared/components/layout/ColocaPageHeader";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { ColocaTabs, type ColocaTabItem } from "@/shared/components/navigation/ColocaTabs";
import { NoAccessView } from "@/shared/components/NoAccessView";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";
import { colocaToast } from "@/shared/utils/toast";

import { ExampleAmountsBar } from "../components/fees/ExampleAmountsBar";
import { ScopeBanner, type ScopeBannerCopy } from "../components/fees/ScopeBanner";
import { OrganizationScopePicker } from "../components/OrganizationScopePicker";
import { EditCorridorTaxDialog, type EditCorridorTaxIntent } from "../components/taxes/EditCorridorTaxDialog";
import { EditTaxDialog, type EditTaxIntent } from "../components/taxes/EditTaxDialog";
import { RemoveOwnTaxDialog } from "../components/taxes/RemoveOwnTaxDialog";
import { TaxChangesFeed } from "../components/taxes/TaxChangesFeed";
import { TaxCorridorHistoryDrawer } from "../components/taxes/TaxCorridorHistoryDrawer";
import { TaxCorridorsSection, type TaxCorridorActions } from "../components/taxes/TaxCorridorsSection";
import { TaxesHowItWorksGuide } from "../components/taxes/TaxesHowItWorksGuide";
import { TaxKpiStrip, type TaxCorridorKpis } from "../components/taxes/TaxKpiStrip";
import { TaxRulesTab } from "../components/taxes/TaxRulesTab";
import { TaxTabIntro, type TaxTabId } from "../components/taxes/TaxTabIntro";
import { useOrganizationNames } from "../hooks/useOrganizationNames";
import { useOrganizationsCatalog } from "../hooks/useOrganizationsCatalog";
import { usePaymentFees } from "../hooks/usePaymentFees";
import { useFeeAuthors, useRoutesCatalog } from "../hooks/usePaymentFeesCatalogs";
import { usePaymentTaxes } from "../hooks/usePaymentTaxes";
import type { PaymentTax } from "../types/paymentTaxes.types";
import { shortId } from "../utils/feeFormat";
import {
  buildTaxCoverage,
  matchesTaxFilter,
  type TaxCorridorCoverage,
  type TaxCorridorFilter,
} from "../utils/taxCoverage";

const TAB_IDS: TaxTabId[] = ["corridors", "history", "rules"];

// Same reading and flow as the price book: the scope picker decides what is shown and who a change reaches.
const PaymentTaxesPage = () => {
  const { t, activeLanguage } = useTranslations("paymentTaxes");
  const { t: tGlobal } = useTranslations("global");
  const { can } = useAuth();
  const canWrite = can("payments:write");

  useAppShellBreadcrumb(
    useMemo(
      () => [
        { label: tGlobal("sidebar_section_gestion") },
        { label: tGlobal("sidebar_menu_payment_taxes"), to: "/app/payments-config/taxes" },
      ],
      [tGlobal],
    ),
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab");
  const tab: TaxTabId = TAB_IDS.includes(rawTab as TaxTabId) ? (rawTab as TaxTabId) : "corridors";
  const setTab = (next: TaxTabId) => {
    const params = new URLSearchParams(searchParams);
    if (next === "corridors") params.delete("tab");
    else params.set("tab", next);
    setSearchParams(params, { replace: true });
  };

  const routesCatalog = useRoutesCatalog();
  const organizationsCatalog = useOrganizationsCatalog();
  const { nameById: authorNames } = useFeeAuthors();
  const taxesState = usePaymentTaxes();
  const feesState = usePaymentFees(routesCatalog.routes, taxesState.rows);
  const scopeOrganizationId = feesState.scopeOrganizationId;
  const scoped = scopeOrganizationId != null;

  const [filter, setFilter] = useState<TaxCorridorFilter>("all");
  const [newTaxIntent, setNewTaxIntent] = useState<EditTaxIntent | null>(null);
  const [editIntent, setEditIntent] = useState<EditCorridorTaxIntent | null>(null);
  const [removeTarget, setRemoveTarget] = useState<TaxCorridorCoverage | null>(null);
  const [historyTarget, setHistoryTarget] = useState<TaxCorridorCoverage | null>(null);

  const organizationIds = useMemo(
    () => [
      scopeOrganizationId,
      ...new Set(taxesState.rows.map((tax) => tax.organization_id).filter(Boolean)),
    ],
    [scopeOrganizationId, taxesState.rows],
  );
  const organizationNames = useOrganizationNames(organizationsCatalog.organizations, organizationIds);
  const scopeOrganizationName = scopeOrganizationId
    ? (organizationNames[scopeOrganizationId] ?? shortId(scopeOrganizationId))
    : null;

  const coverage = useMemo(
    () => buildTaxCoverage(feesState.corridors, taxesState.rows, taxesState.now, scopeOrganizationId),
    [feesState.corridors, taxesState.rows, taxesState.now, scopeOrganizationId],
  );
  const visible = useMemo(
    () => coverage.filter((row) => matchesTaxFilter(row, filter, scoped)),
    [coverage, filter, scoped],
  );
  const kpis = useMemo<TaxCorridorKpis>(
    () => ({
      total: coverage.length,
      taxed: coverage.filter((row) => matchesTaxFilter(row, "taxed", scoped)).length,
      own: coverage.filter((row) => matchesTaxFilter(row, "own", scoped)).length,
      scheduled: coverage.filter((row) => matchesTaxFilter(row, "scheduled", scoped)).length,
    }),
    [coverage, scoped],
  );
  const organizationsWithOwn = useMemo(
    () => new Set(coverage.flatMap((row) => row.overrides.map((override) => override.organizationId))).size,
    [coverage],
  );
  const currencies = useMemo(
    () => [...new Set(routesCatalog.routes.map((route) => route.currency))].sort(),
    [routesCatalog.routes],
  );

  const scopeLabels = useMemo(
    () => ({
      label: t("scope_label"),
      placeholder: t("scope_placeholder"),
      loading: t("scope_loading"),
      empty: t("scope_empty"),
      searching: t("scope_searching"),
      searchError: t("scope_search_error"),
      catalogError: t("scope_catalog_error"),
      catalogTruncated: t("scope_catalog_truncated"),
      globalOption: t("scope_global_option"),
      globalHint: t("scope_global_hint"),
      orgHint: t("scope_org_hint"),
    }),
    [t],
  );
  const bannerCopy = useMemo<ScopeBannerCopy>(
    () => ({
      generalTitle: t("banner_general_title"),
      generalBody: t("banner_general_body"),
      generalOrganizations: (count) => t("banner_general_orgs", { count }),
      organizationTitle: (name) => t("banner_org_title", { name }),
      organizationBody: (name) => t("banner_org_body", { name }),
      backToGeneral: t("banner_back_to_general"),
    }),
    [t],
  );

  const refreshTaxes = taxesState.refresh;
  const refreshAll = useCallback(() => {
    refreshTaxes();
    void feesState.refresh();
  }, [refreshTaxes, feesState]);

  const handleSaved = useCallback(
    (tax: PaymentTax, validFrom: string | null) => {
      setEditIntent(null);
      setNewTaxIntent(null);
      colocaToast.success(
        validFrom
          ? t("toast_scheduled", {
              name: tax.name,
              date: formatDateTime(validFrom, { locale: activeLanguage }),
            })
          : t("toast_saved", { name: tax.name }),
      );
      refreshTaxes();
    },
    [refreshTaxes, t, activeLanguage],
  );

  const corridorActions = useMemo<TaxCorridorActions>(
    () => ({
      onEdit: (row) =>
        setEditIntent({
          coverage: row,
          organizationId: scopeOrganizationId,
          organizationName: scopeOrganizationName,
        }),
      onBackToGeneral: setRemoveTarget,
      onHistory: setHistoryTarget,
      onViewRules: () => setTab("rules"),
      onViewOrganization: feesState.setScopeOrganizationId,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scopeOrganizationId, scopeOrganizationName, feesState.setScopeOrganizationId, searchParams],
  );

  if (!taxesState.hasAccess || !feesState.hasAccess) return <NoAccessView />;

  const tabs: ColocaTabItem[] = [
    { value: "corridors", label: t("tab_corridors"), content: null },
    { value: "history", label: t("tab_history"), content: null },
    { value: "rules", label: t("tab_rules"), content: null },
  ];
  const routesUnavailable = routesCatalog.hasError || routesCatalog.accessDenied;
  const isLoading = taxesState.isLoading || feesState.isLoading || routesCatalog.isLoading;

  return (
    <div className="space-y-6 p-2">
      <ColocaPageHeader
        title={t("page_title")}
        description={t("page_subtitle")}
        actions={
          <>
            <ColocaButton
              variant="default"
              size="sm"
              icon={RefreshCw}
              aria-label={t("refresh_aria")}
              className={cn((taxesState.isRefreshing || feesState.isRefreshing) && "[&_svg]:animate-spin")}
              onClick={refreshAll}
            >
              {t("refresh_button")}
            </ColocaButton>
            {canWrite && !scoped && (
              <ColocaButton
                variant="default"
                size="sm"
                icon={Plus}
                onClick={() =>
                  setNewTaxIntent({ source: null, lockScope: false, organizationId: null, current: null })
                }
              >
                {t("new_tax_button")}
              </ColocaButton>
            )}
          </>
        }
      />

      <TaxesHowItWorksGuide />

      <div className="space-y-3">
        <OrganizationScopePicker
          id="payment-taxes-scope"
          className="max-w-md"
          value={scopeOrganizationId}
          onValueChange={feesState.setScopeOrganizationId}
          catalog={organizationsCatalog}
          labels={scopeLabels}
        />
        <ScopeBanner
          organizationName={scopeOrganizationName}
          specialPriceOrganizations={organizationsWithOwn}
          onBackToGeneral={() => feesState.setScopeOrganizationId(null)}
          copy={bannerCopy}
        />
      </div>

      {taxesState.hasError || feesState.hasError ? (
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          heading={t("error_load")}
          text={t("error_load_detail")}
          actions={
            <ColocaButton variant="default" size="sm" icon={RefreshCw} onClick={refreshAll}>
              {t("error_retry")}
            </ColocaButton>
          }
        />
      ) : (
        <>
          <ColocaTabs
            tabs={tabs}
            variant="line"
            value={tab}
            onValueChange={(value) => setTab(value as TaxTabId)}
            listClassName="overflow-x-auto"
          />

          <TaxTabIntro tab={tab} />

          {tab === "corridors" && (
            <div className="space-y-4">
              {isLoading ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  {[0, 1, 2, 3].map((index) => (
                    <ColocaSkeleton key={index} className="h-[5.5rem] rounded-xl" />
                  ))}
                </div>
              ) : (
                <TaxKpiStrip
                  kpis={kpis}
                  filter={filter}
                  onFilterChange={setFilter}
                  scoped={scoped}
                  unavailable={routesUnavailable}
                />
              )}
              <ExampleAmountsBar
                currencies={currencies}
                exampleAmountFor={feesState.exampleAmountFor}
                onChange={feesState.setExampleAmount}
                onReset={feesState.resetExampleAmount}
              />
              <TaxCorridorsSection
                corridors={visible}
                totalCount={coverage.length}
                isLoading={isLoading}
                routesError={routesUnavailable}
                onRetryRoutes={routesCatalog.refresh}
                scopeOrganizationId={scopeOrganizationId}
                organizationNames={organizationNames}
                canWrite={canWrite}
                {...corridorActions}
              />
              <ColocaAlert variant="secondary" icon={AlertCircle} text={t("why_banner")} />
            </div>
          )}

          {tab === "history" && (
            <TaxChangesFeed
              rows={taxesState.rows}
              now={taxesState.now}
              isLoading={taxesState.isLoading}
              scopeOrganizationId={scopeOrganizationId}
              organizationNames={organizationNames}
              authorNames={authorNames}
            />
          )}

          {tab === "rules" && (
            <TaxRulesTab
              state={taxesState}
              scopeOrgId={scopeOrganizationId}
              orgName={scopeOrganizationName ?? undefined}
              canWrite={canWrite}
              onEditTax={setNewTaxIntent}
            />
          )}
        </>
      )}

      {editIntent && (
        <EditCorridorTaxDialog
          intent={editIntent}
          onClose={() => setEditIntent(null)}
          onSaved={handleSaved}
          onCreateTax={() => {
            setEditIntent(null);
            setNewTaxIntent({ source: null, lockScope: false, organizationId: null, current: null });
          }}
          rows={taxesState.rows}
          routes={routesCatalog.routes}
          corridors={feesState.corridors}
          now={taxesState.now}
          organizationNames={organizationNames}
        />
      )}

      {newTaxIntent && (
        <EditTaxDialog
          intent={newTaxIntent}
          onClose={() => setNewTaxIntent(null)}
          onSaved={(tax) => handleSaved(tax, null)}
        />
      )}

      {removeTarget && scopeOrganizationId && (
        <RemoveOwnTaxDialog
          route={removeTarget.corridor.route}
          organizationId={scopeOrganizationId}
          organizationName={scopeOrganizationName ?? scopeOrganizationId}
          onClose={() => setRemoveTarget(null)}
          onRemoved={() => {
            colocaToast.success(t("toast_removed", { name: scopeOrganizationName ?? scopeOrganizationId }));
            setRemoveTarget(null);
            refreshTaxes();
          }}
          rows={taxesState.rows}
          routes={routesCatalog.routes}
          now={taxesState.now}
        />
      )}

      {historyTarget && (
        <TaxCorridorHistoryDrawer
          route={historyTarget.corridor.route}
          organizationId={scopeOrganizationId}
          organizationName={scopeOrganizationName}
          onClose={() => setHistoryTarget(null)}
          rows={taxesState.rows}
          now={taxesState.now}
          organizationNames={organizationNames}
          authorNames={authorNames}
        />
      )}
    </div>
  );
};

export default PaymentTaxesPage;
